import { geoLabel } from '@/components/companion/preference-labels';
import { useContactProfileQuery } from '@/hooks/queries/use-contact-profile-query';
import { useTranslation } from 'react-i18next';
import type { ContactPlatform } from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import { platformEnumOf } from '../platform-of';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

/**
 * The location line under the contact's name in the rail header, or `null`
 * when nothing measured or declared says where the creator lives.
 *
 * Reads the drawer's cached profile slice (the same query key the profile view
 * and the header avatar already use, so it adds no request): the city only when
 * the creator registered and declared it, as the profile view shows it, and the
 * creator's country as the dataset reports it (an ISO region code, named in the
 * UI locale). A prospect whose identity the plan withholds shows none.
 */
export const useContactLocation = (
  contact: ContactShell,
  platform: ContactPlatform
): string | null => {
  const { i18n } = useTranslation();
  const creatorIdentityId = useResolvedContactIdentityId();
  const { data } = useContactProfileQuery(creatorIdentityId, platformEnumOf(platform));

  if (!data || contact.locked?.identity) return null;

  const city = data.registered === true ? data.city?.trim() : undefined;
  const countryCode = data.country?.trim();
  const country = countryCode ? geoLabel(countryCode, i18n.language) : undefined;
  const parts = [city, country].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : null;
};
